import productMatrixConfig from "@/constants/productMatrix.json";
import type { Device, SimCardDetail } from "@/services/model";
import { SimCustomerInfoCreditClass } from "@/services/model";
import type { PrepareBasketRequestDevice } from "@/services/model/prepareBasketRequestDevice";
import { shouldFetchCockpitRoamingForSim } from "@/utils/cockpitRoamingFetch";

export type ProductCategory = "mobile" | "home" | "data";

/** Keys of `TariffTypes` (deviceUtils): the tier a product belongs to. */
export type TariffTypeKey =
  | "flatMobileOne"
  | "flatMobileTwo"
  | "flatMobileThree"
  | "flatHome4G"
  | "flatHome5G"
  | "dataTariff";

type CategoryConfig = {
  deviceId: number;
  deviceDescription: string;
  deviceEnum: PrepareBasketRequestDevice;
};

type ProductSpeed = {
  download?: number;
  upload?: number;
  fullspeed?: boolean;
};

type InternationalCallsSec = "notIncluded" | "unlimited" | `${number}`;

type ProductConfig = {
  category: ProductCategory;
  tariffType: TariffTypeKey;
  defaultAliasKey: string;
  download?: number;
  upload?: number;
  fullspeed?: boolean;
  internationalCallsSec?: InternationalCallsSec;
  /** Included EU/USA roaming data in marketed GB (Flat Mobile One/Two/Three). */
  roamingDataGb?: number;
  /** Backend roamingProduct id expected for this Flat Mobile tariff's included package. */
  roamingProduct?: string;
  attributes?: Record<string, unknown>;
};

type ProductDevice = {
  id: number;
  description: string;
  enum: PrepareBasketRequestDevice;
};

const categories = productMatrixConfig.categories as Record<
  ProductCategory,
  CategoryConfig
>;

const products = productMatrixConfig.products as Record<string, ProductConfig>;

const getProduct = (productId?: number): ProductConfig | undefined =>
  productId ? products[String(productId)] : undefined;

export const getProductCategory = (productId?: number): ProductCategory => {
  if (!productId) {
    return "mobile";
  }

  return getProduct(productId)?.category ?? "data";
};

/**
 * Whether the matrix lists the product under `category` - the one place that
 * says which service ids are Flat Mobile, Flat Home or data-only.
 */
export const isProductInCategory = (
  productId: number | undefined,
  category: ProductCategory,
): boolean => getProduct(productId)?.category === category;

/** The product's tier, e.g. "flatMobileOne"; undefined for unknown ids. */
export const getTariffTypeKey = (
  productId?: number,
): TariffTypeKey | undefined => getProduct(productId)?.tariffType;

export const getDeviceForProduct = (productId?: number): ProductDevice => {
  const category = getProductCategory(productId);
  const config = categories[category];

  return {
    id: config.deviceId,
    description: config.deviceDescription,
    enum: config.deviceEnum,
  };
};

// Device ids outside the categories config (tablet, GPS tracker, …) come from older orders.
export const getCategoryForDevice = (device?: Device): ProductCategory => {
  if (device?.id === undefined) {
    return "mobile";
  }

  const category = (Object.keys(categories) as ProductCategory[]).find(
    (key) => categories[key].deviceId === device.id,
  );

  return category ?? "data";
};

export const getDefaultAliasKey = (productId?: number): string =>
  getProduct(productId)?.defaultAliasKey ?? "";

export const getProductSpeed = (
  productId?: number,
): ProductSpeed | undefined => {
  const product = getProduct(productId);
  if (!product) {
    return undefined;
  }

  const { download, upload, fullspeed } = product;
  if (download === undefined && upload === undefined && !fullspeed) {
    return undefined;
  }

  return { download, upload, fullspeed };
};

const getInternationalCallsSec = (
  productId?: number,
): InternationalCallsSec | undefined =>
  getProduct(productId)?.internationalCallsSec;

/** Marketed included roaming data volume in GB for Flat Mobile products. */
export const getRoamingDataGb = (productId?: number): number | undefined =>
  getProduct(productId)?.roamingDataGb;

/** Every marketed included roaming volume in GB (3, 20, 50). */
export const getMarketedRoamingVolumesGb = (): number[] => [
  ...new Set(
    Object.values(products)
      .map((product) => product.roamingDataGb)
      .filter((volume): volume is number => volume !== undefined),
  ),
];

/**
 * Backend `roamingProduct` id expected for the included Flat Mobile roaming
 * package (e.g. "mvno.1082" for Flat Mobile One).
 */
export const getRoamingProduct = (productId?: number): string | undefined =>
  getProduct(productId)?.roamingProduct;

type RoamingCallPackageUsageSkipReason =
  "creditClassC" | "notIncluded" | "unlimited";

type RoamingCallPackageUsageDecision =
  | { shouldFetch: false; reason: RoamingCallPackageUsageSkipReason }
  | { shouldFetch: true; allowanceSec: number };

type InternationalCallsTileDisplay =
  | { kind: "hidden" }
  | { kind: "notAllowed" }
  | { kind: "unlimited" }
  | { kind: "usage"; allowanceSec: number };

const productIncludesInternationalCalls = (
  internationalCallsSec: InternationalCallsSec | undefined,
): internationalCallsSec is Exclude<InternationalCallsSec, "notIncluded"> =>
  !!internationalCallsSec && internationalCallsSec !== "notIncluded";

/** Whether the product includes EU/USA calls (Flat Mobile One/Two/Three). */
export const includesInternationalCalls = (productId?: number): boolean =>
  productIncludesInternationalCalls(getInternationalCallsSec(productId));

/**
 * Included EU/USA calling minutes over the product's term, e.g. 30 for Flat
 * Mobile One monthly and 360 yearly; "unlimited" for Flat Mobile Three.
 */
export const getIncludedCallMinutes = (
  productId?: number,
): number | "unlimited" | undefined => {
  const internationalCallsSec = getInternationalCallsSec(productId);

  if (!productIncludesInternationalCalls(internationalCallsSec)) {
    return undefined;
  }

  return internationalCallsSec === "unlimited"
    ? "unlimited"
    : Number(internationalCallsSec) / 60;
};

/**
 * Tile display for "Calls EU/USA".
 * 1. Credit class C → still show the row as unavailable when the product includes calls
 * 2. Else use productMatrix `internationalCallsSec`:
 *    - notIncluded → hide
 *    - unlimited (Flat Mobile Three) → unlimited
 *    - finite seconds → usage bar (API-backed)
 */
export const getInternationalCallsTileDisplay = ({
  productId,
  creditClass,
}: {
  productId?: number;
  creditClass?: SimCustomerInfoCreditClass | string | null;
}): InternationalCallsTileDisplay => {
  const internationalCallsSec = getInternationalCallsSec(productId);

  if (creditClass === SimCustomerInfoCreditClass.CreditClassC) {
    return productIncludesInternationalCalls(internationalCallsSec)
      ? { kind: "notAllowed" }
      : { kind: "hidden" };
  }

  if (!productIncludesInternationalCalls(internationalCallsSec)) {
    return { kind: "hidden" };
  }

  if (internationalCallsSec === "unlimited") {
    return { kind: "unlimited" };
  }

  return {
    kind: "usage",
    allowanceSec: Number(internationalCallsSec),
  };
};

/**
 * Decides whether `/sim/roamingCallPackageUsage` is needed.
 * Skip when: credit class C, product has no included minutes, or allowance is unlimited.
 * Fetch only for finite included allowances (e.g. One 1800 / Two 6000 sec).
 */
export const getRoamingCallPackageUsageDecision = ({
  productId,
  creditClass,
}: {
  productId?: number;
  creditClass?: SimCustomerInfoCreditClass | string | null;
}): RoamingCallPackageUsageDecision => {
  if (creditClass === SimCustomerInfoCreditClass.CreditClassC) {
    return { shouldFetch: false, reason: "creditClassC" };
  }

  const display = getInternationalCallsTileDisplay({ productId, creditClass });

  if (display.kind === "hidden") {
    return { shouldFetch: false, reason: "notIncluded" };
  }

  if (display.kind === "unlimited") {
    return { shouldFetch: false, reason: "unlimited" };
  }

  if (display.kind === "notAllowed") {
    return { shouldFetch: false, reason: "creditClassC" };
  }

  return {
    shouldFetch: true,
    allowanceSec: display.allowanceSec,
  };
};

export const shouldFetchRoamingCallPackageUsage = (params: {
  productId?: number;
  creditClass?: SimCustomerInfoCreditClass | string | null;
}): boolean => getRoamingCallPackageUsageDecision(params).shouldFetch;

/** SIM IDs that need a roaming-call usage fetch for cockpit tiles. */
export const getSimIdsNeedingRoamingCallPackageUsage = (
  sims: SimCardDetail[],
  creditClass?: SimCustomerInfoCreditClass | string | null,
  needsIdCheck?: boolean,
): number[] => {
  if (creditClass === SimCustomerInfoCreditClass.CreditClassC) {
    return [];
  }

  return sims
    .filter(
      (sim) =>
        shouldFetchCockpitRoamingForSim(sim, needsIdCheck) &&
        shouldFetchRoamingCallPackageUsage({
          productId: getProductIdFromSim(sim),
          creditClass,
        }),
    )
    .map((sim) => sim.simId)
    .filter((simId): simId is number => simId !== undefined);
};

export const getProductIdFromSim = (sim?: SimCardDetail): number | undefined =>
  sim?.serviceID ?? sim?.successorForRenew?.id;
